import { faqHomeBreadcrumb, homeBreadcrumb } from '@config'
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types'
import { ContentType } from '@entries/content-types'
import ContentfulAsset from '@entries/contentful-asset'
import ContentfulBlogArticleLink from '@entries/contentful-blog-article-link'
import ContentfulDeprecatedPageLink from '@entries/contentful-deprecated-page-link'
import ContentfulExternalLink from '@entries/contentful-external-link'
import ContentfulFaqPageLink from '@entries/contentful-faq-page-link'
import ContentfulFaqSubCategoryPageClickableChip from '@entries/contentful-faq-sub-category-page-clickable-chip'
import ContentfulFootnoteSup from '@entries/contentful-footnote-sup'
import ContentfulPDFLink from '@entries/contentful-pdf-link'
import ContentfulStaticPageLink from '@entries/contentful-static-page-link'
import ContentfulSubscriptionLink from '@entries/contentful-subscription-link'
import ContentfulYoutubePlayer from '@entries/contentful-youtube-player'
import DefaultComponent from '@entries/default-component'
import { withDisplayName } from '@hocs/with-display-name'
import { FaqPageTemplate } from '@layouts/faq-page'
import { bold } from '@render-mark/bold'
import { italic } from '@render-mark/italic'
import { breakNode } from '@render-node/break'
import { deprecatedHeading2 } from '@render-node/deprecated-heading-2'
import { deprecatedParagraph } from '@render-node/deprecated-paragraph'
import { hr } from '@render-node/hr'
import { html } from '@render-node/html'
import { listItem } from '@render-node/list-item'
import { olList } from '@render-node/ol-list'
import { table } from '@render-node/table'
import { tableCell } from '@render-node/table-cell'
import { tableHeaderCell } from '@render-node/table-header-cell'
import { tableRow } from '@render-node/table-row'
import { ulList } from '@render-node/ul-list'
import { Entry } from '@resolvers/entry'
import RichText from '@resolvers/rich-text'
import { CUSTOMS } from '@resolvers/rich-text/types'
import Loader from '@ui/atoms/loader'
import { Container } from '@ui/container'
import { graphql } from 'gatsby'
import React from 'react'
import MopinionForm from 'react-mopinion-forms'

export const answerRichTextOptions = {
  renderMark: {
    [MARKS.ITALIC]: italic,
    [MARKS.BOLD]: bold,
  },
  renderNode: {
    [CUSTOMS.BREAK]: breakNode,
    [CUSTOMS.HTML]: html,
    [CUSTOMS.TABLE_CELL]: tableCell,
    [BLOCKS.TABLE]: table,
    [BLOCKS.TABLE_ROW]: tableRow,
    [BLOCKS.TABLE_HEADER_CELL]: tableHeaderCell,
    [BLOCKS.TABLE_CELL]: tableCell,
    [BLOCKS.HEADING_2]: deprecatedHeading2,
    [BLOCKS.OL_LIST]: olList,
    [BLOCKS.UL_LIST]: ulList,
    [BLOCKS.LIST_ITEM]: listItem,
    [BLOCKS.HR]: hr,
    [BLOCKS.PARAGRAPH]: deprecatedParagraph,
    [INLINES.ENTRY_HYPERLINK]: {
      [ContentType.ContentfulBlogArticle]: ContentfulBlogArticleLink,
      [ContentType.ContentfulDeprecatedPage]: ContentfulDeprecatedPageLink,
      [ContentType.ContentfulStaticPage]: ContentfulStaticPageLink,
      [ContentType.ContentfulPdfLink]: ContentfulPDFLink,
      [ContentType.ContentfulExternalLink]: ContentfulExternalLink,
      [ContentType.ContentfulFaqPage]: ContentfulFaqPageLink,
      [ContentType.ContentfulSubscriptionLink]: ContentfulSubscriptionLink,
      default: DefaultComponent,
    },
    [BLOCKS.EMBEDDED_ENTRY]: {
      [ContentType.ContentfulAsset]: ContentfulAsset,
      [ContentType.ContentfulYoutubePlayer]: ContentfulYoutubePlayer,
      default: DefaultComponent,
    },
    [INLINES.EMBEDDED_ENTRY]: {
      [ContentType.ContentfulFootnote]: ContentfulFootnoteSup,
      default: DefaultComponent,
    },
    [BLOCKS.EMBEDDED_ASSET]: {
      [ContentType.ContentfulAsset]: ContentfulAsset,
      default: DefaultComponent,
    },
  },
}

const ContentfulFaqPageTemplate: Utils.EntryFC<
  Queries.ContentfulFaqPageTemplateFragment
> = ({ content: { subCategoriesRef, title, href, answer } }) => {
  const pageBreadcrumb = {
    name: title,
    href,
  }

  const breadcrumbWithHome = [homeBreadcrumb, faqHomeBreadcrumb, pageBreadcrumb]

  return (
    <FaqPageTemplate breadcrumbs={breadcrumbWithHome}>
      <div className="mb-5 flex justify-center gap-3">
        {subCategoriesRef?.map((subCategory, i) => (
          <Entry
            key={i}
            content={subCategory}
            mapping={{
              [ContentType.ContentfulFaqSubCategoryPage]:
                ContentfulFaqSubCategoryPageClickableChip,
            }}
          />
        ))}
      </div>

      <h1 className="mb-8 text-balance text-center text-200">{title}</h1>

      <Container className="group/content-type faq-page-template-block px-0">
        <RichText content={answer} options={answerRichTextOptions} />
      </Container>
      <div className="mt-16 flex flex-col" id="mopinion-feedback">
        <Loader className="self-center" id="ftn-loader" />
        <MopinionForm
          domain="arkea.mopinion.com"
          formKey="c1ef0ee6e2de05fc71408e6ce5584f3b15eda1c5"
        />
      </div>
    </FaqPageTemplate>
  )
}

export const query = graphql`
  fragment ContentfulSubCategory on ContentfulFaqSubCategoryPage {
    ...ContentfulEntry
    href: getHref
    title
  }

  fragment ContentfulFaqPageAnswer on ContentfulFaqPageAnswer {
    rich
    references {
      ...ContentfulBlogArticleLink
      ...ContentfulPDFLink
      ...ContentfulDeprecatedPageLink
      ...ContentfulExternalLink
      ...ContentfulFaqPageLink
      ...ContentfulStaticPageLink
      ...ContentfulSubscriptionLink
      ...ContentfulDefaultAsset
      ...ContentfulYoutubePlayer
    }
  }

  fragment ContentfulFaqPageTemplate on ContentfulFaqPage {
    ...ContentfulEntry
    subCategoriesRef {
      ...ContentfulSubCategory
    }
    answer {
      ...ContentfulFaqPageAnswer
    }
    href: getHref
    title
  }
`

export default withDisplayName(
  ContentfulFaqPageTemplate,
  'ContentfulFaqPageTemplate'
)
